// 🔧 demo_builder.js
// ✅ Purpose: Super admin-only Platform tool for creating reusable Let's Go Vote demo events
// ✅ Features:
// - Creates LGV demo events using the existing shared demo event helper
// - Starts the formal LGV Demo Engine UI architecture
// - Supports reusable industry-specific Experiences, including Car Show and SAP AI Agents Challenge
// - Supports demo intent selection: Internal Testing or Customer Demo
// - Supports demo size selection for future scaled seed data
// - Shows future-ready Core Package and Add-on checklist sections
// - Keeps add-ons disabled for now until seed builders are added
// - Shows success and error notifications at the top of the page content for 20 seconds
// - Uses LGV logger for debugging
// - Fully block-commented for clarity and future devs

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  endAt,
  getDocs,
  limit,
  orderBy,
  query,
  startAt,
  where,
} from "firebase/firestore";
import FullPageSpinner from "../../components/FullPageSpinner";
import { useAuth } from "../../auth_context";
import { db } from "../../firebase";
import { logger } from "../../utils/logger";
import { createDemo } from "../../demo_engine";
import { INDUSTRIES } from "../../demo_engine/industries";
import { DEMO_SIZE_CONFIG } from "../../demo_engine/config/demo_sizes";
import {
  DEMO_ADDON_OPTIONS,
  DEMO_INTENT_OPTIONS,
  createDefaultAddOnSelection,
} from "../../demo_engine/config/demo_options";
import {
  cleanupFailedExperience,
  deleteExperience,
} from "../../demo_engine/run_tracking/experience_run_tracker";
import {
  VOTING_TYPE_OPTIONS,
  DEFAULT_VOTING_TYPE,
} from "../../utils/voting_types";

// =====================================================
// 🏭 Demo Industry Templates
// =====================================================
const DEMO_INDUSTRIES = INDUSTRIES;

// =====================================================
// 🎯 Demo Intent Options
// =====================================================
const DEMO_INTENTS = DEMO_INTENT_OPTIONS;

// =====================================================
// 📏 Demo Size Options
// =====================================================
const DEMO_SIZES = DEMO_SIZE_CONFIG;

const CUSTOMER_DEMO_OWNER = Object.freeze({
  name: "Cesar Villaca",
  email: "cesar.villaca@gmail.com",
  phone: "971.300.7063",
});

// Keep demo locations plausible without tying a temporary Experience to a real
// organizer, website, or venue.
const DEMO_EVENT_LOCATIONS = Object.freeze([
  "Portland, Oregon",
  "Austin, Texas",
  "Nashville, Tennessee",
  "Denver, Colorado",
  "Madison, Wisconsin",
  "Seattle, Washington",
  "Riverfront Expo Center",
  "Downtown Community Pavilion",
  "Heritage Park Event Lawn",
  "Civic Center Grand Hall",
  "Lakeside Fairgrounds",
  "The Foundry Event Space",
]);

const getRandomItem = (items) =>
  items[Math.floor(Math.random() * items.length)];

const createNewDemoDefaults = () => {
  const randomIndustry = getRandomItem(
    Object.values(DEMO_INDUSTRIES).filter(
      (industry) =>
        industry.enabled !== false && !industry.scoring?.votingType
    )
  );
  const randomVotingType = getRandomItem(
    VOTING_TYPE_OPTIONS.filter((type) => type.value !== "scorecard")
  );

  return {
    industry: randomIndustry?.key || "car_show",
    votingType: randomVotingType?.value || DEFAULT_VOTING_TYPE,
    eventLocation: getRandomItem(DEMO_EVENT_LOCATIONS),
  };
};

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getTodayInputValue = () => formatDateInputValue(new Date());

const getYesterdayInputValue = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return formatDateInputValue(yesterday);
};

const getFutureDateInputValue = (daysFromToday) => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromToday);
  return formatDateInputValue(date);
};

// =====================================================
// ✅ Core Package Checklist
// =====================================================
const CORE_PACKAGE_ITEMS = [
  "Event setup",
  "Categories",
  "Entries",
  "Users/Voters",
  "Voting",
  "QR codes",
  "Ballots",
  "Leaderboard",
  "Winners",
  "Dashboard",
  "Audit tools",
  "Persistent event homepage",
  "Advanced SEO",
  "Social sharing previews",
  "Maps",
  "Schedule",
  "Volunteers",
];

// =====================================================
// 💰 Power Ups
// -----------------------------------------------------
// Volunteers are part of the Core Package.
// These add-ons can be enabled during demo creation.
// =====================================================
const DEMO_ADDONS = DEMO_ADDON_OPTIONS;

const DemoBuilder = () => {

  const { user } = useAuth();
  const [newDemoDefaults] = useState(createNewDemoDefaults);

  const [selectedIndustry, setSelectedIndustry] = useState(
    newDemoDefaults.industry
  );
  const [selectedIntent, setSelectedIntent] = useState("customer_demo");
  const [eventTitle, setEventTitle] = useState("");
  const [eventOwnerName] = useState(CUSTOMER_DEMO_OWNER.name);
  const [eventOwnerEmail] = useState(CUSTOMER_DEMO_OWNER.email);
  const [eventOwnerPhone] = useState(CUSTOMER_DEMO_OWNER.phone);
  const [eventStartDate, setEventStartDate] = useState(() =>
    getFutureDateInputValue(5)
  );
  const [eventLocation, setEventLocation] = useState(
    newDemoDefaults.eventLocation
  );
  const [selectedDemoSize, setSelectedDemoSize] = useState("medium");
  const [selectedVotingType, setSelectedVotingType] = useState(
    newDemoDefaults.votingType
  );
  const [demoReport, setDemoReport] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [status, setStatus] = useState("");
  const [cleanupTarget, setCleanupTarget] = useState(null);
  const [demoSites, setDemoSites] = useState([]);
  const [demoSitesLoading, setDemoSitesLoading] = useState(true);
  const [openConfigurationPanel, setOpenConfigurationPanel] = useState("powerups");

  // =====================================================
  // Power Ups
  // =====================================================
  const [selectedAddOns, setSelectedAddOns] = useState(
    createDefaultAddOnSelection
  );

  const loadDemoSites = useCallback(async () => {
    try {
      setDemoSitesLoading(true);
      const eventsCollection = collection(db, "events");
      const [demoSnapshot, legacySlugSnapshot, legacyStatusSnapshot] =
        await Promise.all([
          getDocs(
            query(
              eventsCollection,
              where("demo", "==", true),
              limit(100)
            )
          ),
          getDocs(
            query(
              eventsCollection,
              orderBy("slug"),
              startAt("demo-"),
              endAt("demo-\uf8ff"),
              limit(100)
            )
          ),
          getDocs(
            query(
              eventsCollection,
              where("status", "==", "Demo"),
              limit(100)
            )
          ),
        ]);

      const demoSiteDocuments = new Map();
      [demoSnapshot, legacySlugSnapshot, legacyStatusSnapshot].forEach(
        (snapshot) => {
          snapshot.docs.forEach((eventDocument) => {
            demoSiteDocuments.set(eventDocument.id, eventDocument);
          });
        }
      );

      setDemoSites(
        Array.from(demoSiteDocuments.values())
          .map((eventDocument) => ({
            id: eventDocument.id,
            ...eventDocument.data(),
          }))
          .sort((left, right) => {
            const leftTime =
              left.demoCreatedAt?.toMillis?.() ||
              left.createdAt?.toMillis?.() ||
              0;
            const rightTime =
              right.demoCreatedAt?.toMillis?.() ||
              right.createdAt?.toMillis?.() ||
              0;
            return rightTime - leftTime;
          })
      );
    } catch (error) {
      logger.error("Failed to load demo sites", { value: error });
      setStatus(`❌ Failed to load demo sites: ${error?.message || "Unknown error."}`);
    } finally {
      setDemoSitesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDemoSites();
  }, [loadDemoSites]);

  // =====================================================
  // ⏱ Auto-clear status message
  // =====================================================
  useEffect(() => {
    if (!status) return;

    const timer = setTimeout(() => {
      setStatus("");
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  // =====================================================
  // 🧠 Selected option helpers
  // =====================================================
  const industryTemplate = useMemo(() => {
    return DEMO_INDUSTRIES[selectedIndustry] || DEMO_INDUSTRIES.car_show;
  }, [selectedIndustry]);

  const sizeTemplate = useMemo(() => {
    return DEMO_SIZES[selectedDemoSize] || DEMO_SIZES.small;
  }, [selectedDemoSize]);

  const hasFixedExperienceSize =
    Number.isInteger(industryTemplate?.fixedEntryCount) &&
    industryTemplate.fixedEntryCount > 0;

  useEffect(() => {
    if (industryTemplate?.scoring?.votingType) {
      setSelectedVotingType(industryTemplate.scoring.votingType);
    }
  }, [industryTemplate]);

  useEffect(() => {
    if (
      selectedIntent === "live_example" &&
      (!eventStartDate || eventStartDate >= getTodayInputValue())
    ) {
      setEventStartDate(getYesterdayInputValue());
    }
  }, [eventStartDate, selectedIntent]);

  const renderReportValue = (value) => {
    if (value === null || value === undefined) {
      return "—";
    }

    if (typeof value === "object") {
      return Object.entries(value)
        .map(([key, itemValue]) => `${key}: ${itemValue}`)
        .join(" • ");
    }

    return String(value);
  };

  // =====================================================
  // 🧪 Create Demo Event
  // =====================================================
  const handleCreateDemoEvent = async () => {
    const requiredFields = [
      eventOwnerName,
      eventOwnerEmail,
      eventOwnerPhone,
      eventStartDate,
      eventLocation,
    ];

    if (requiredFields.some((value) => !value.trim())) {
      setStatus(
        "❌ Event Owner Name, Event Owner Email, Event Owner Phone, Start Date, and Event Location are required."
      );
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(eventOwnerEmail.trim())) {
      setStatus("❌ Enter a valid Event Owner Email.");
      return;
    }

    if (
      selectedIntent === "live_example" &&
      eventStartDate >= getTodayInputValue()
    ) {
      setStatus("❌ Live Example Start Date must be in the past.");
      return;
    }

    try {
      setProcessing(true);
      setProcessingMessage("Creating LGV demo event...");
      setDemoReport(null);
      setCleanupTarget(null);

      logger.log("🧪 Starting LGV Demo Engine request:", {
        value: {
          selectedIndustry,
          selectedIntent,
          selectedDemoSize,
          selectedVotingType,
          plannedEntryCount:
            industryTemplate?.fixedEntryCount || sizeTemplate.entryCount,
          userId: user?.uid || null,
          addOns: selectedAddOns
        },
      });

      const result = await createDemo({
        user,
        demoConfig: {
          industry: selectedIndustry,
          intent: selectedIntent,
          eventTitle: eventTitle.trim(),
          eventOwner: {
            name: eventOwnerName.trim(),
            email: eventOwnerEmail.trim(),
            phone: eventOwnerPhone.trim(),
          },
          eventStartDate,
          eventLocation: eventLocation.trim(),
          package: "core",
          votingEngine: selectedVotingType || DEFAULT_VOTING_TYPE,
          size: selectedDemoSize,
          addOns: selectedAddOns,
        },
        onProgress: ({ current, total, label }) => {
          setProcessingMessage(
            `${current}/${total} ${label || "Building Experience"}...`
          );
        },
      });
      setDemoReport(result?.demoEngine?.report || null);

      logger.log(
        "🧪 Demo Engine Report",
        {
          value:
            result?.demoEngine?.report
              ?.executionReport,
        }
      );

      logger.log("✅ LGV demo event created:", {
        value: {
          result,
          selectedIndustry,
          selectedIntent,
          selectedDemoSize,
          selectedVotingType,
          addOns: selectedAddOns
        },
      });

      setStatus(
        `⚠️ Demo event created: ${result.eventName}. Volunteer email and SMS notifications are unavailable for demo Experiences.`
      );
      setEventTitle("");
      await loadDemoSites();
    } catch (err) {
      logger.warn("Failed to create demo event:", {
        value: err,
      });

      const cleanupMessage =
        err?.experienceCleanup === "cleaned"
          ? " The partial Experience was removed automatically."
          : err?.experienceCleanup === "cleanup_required"
            ? " Cleanup is required; the failed run was saved for review."
            : "";

      setStatus(
        `❌ Failed to create demo event: ${err?.message || "Unknown error."}${cleanupMessage}`
      );

      if (
        err?.experienceCleanup === "cleanup_required" &&
        err?.experienceEventId
      ) {
        setCleanupTarget({
          eventId: err.experienceEventId,
          runId: err.experienceRunId,
        });
      }
    } finally {
      setProcessing(false);
      setProcessingMessage("");
    }
  };

  const handleCleanupFailedExperience = async () => {
    if (!cleanupTarget?.eventId) return;

    try {
      setProcessing(true);
      setProcessingMessage("Removing partial Experience...");
      await cleanupFailedExperience(cleanupTarget);
      setCleanupTarget(null);
      setStatus("✅ Partial Experience removed successfully.");
    } catch (error) {
      logger.error("Failed to remove partial Experience", { value: error });
      setStatus(`❌ Cleanup failed: ${error?.message || "Unknown error."}`);
    } finally {
      setProcessing(false);
      setProcessingMessage("");
    }
  };

  const handleDeleteDemoSite = async (site) => {
    const confirmed = window.confirm(
      `Delete "${site.name || "this demo"}" and all of its data? This cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setProcessing(true);
      setProcessingMessage(`Deleting ${site.name || "demo site"}...`);
      await deleteExperience({
        eventId: site.id,
        runId: site.demoRunId || null,
      });
      setDemoSites((current) => current.filter((item) => item.id !== site.id));
      setStatus(`✅ Demo site deleted: ${site.name || site.slug || site.id}`);
    } catch (error) {
      logger.error("Failed to delete demo site", { value: error });
      setStatus(`❌ Failed to delete demo site: ${error?.message || "Unknown error."}`);
    } finally {
      setProcessing(false);
      setProcessingMessage("");
    }
  };

  const formatTimestamp = (value, fallback = "—") => {
    const date = value?.toDate?.();
    return date ? date.toLocaleString() : fallback;
  };

  if (processing) {
    return (
      <FullPageSpinner message={processingMessage || "Creating demo event..."} />
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        <div className="row mb-4">
          <div className="col-12">
            <h1 className="display-5 mb-2">🧪 Experience Engine</h1>

            <p className="lead text-muted mb-0">
              Create reusable industry-specific LGV demo events for testing,
              validation, training, and future customer demos.
            </p>
          </div>

        </div>

        {status && (
          <div
            className={`alert ${
              status.startsWith("✅")
                ? "alert-success"
                : status.startsWith("⚠️")
                  ? "alert-warning"
                  : "alert-danger"
            } mb-4`}
          >
            {status}
            {cleanupTarget && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger ms-3"
                onClick={handleCleanupFailedExperience}
              >
                Remove Partial Experience
              </button>
            )}
          </div>
        )}

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Demo Engine Setup</strong>
          </div>

          <div className="card-body">
            <div className="row g-4">
              <div className="col-12">
                <label className="form-label" htmlFor="demo-event-title">
                  Event Title
                </label>

                <input
                  id="demo-event-title"
                  className="form-control"
                  type="text"
                  value={eventTitle}
                  onChange={(event) => setEventTitle(event.target.value)}
                  placeholder={`Example: ${industryTemplate?.eventNames?.[0] || industryTemplate?.experience?.eventName || industryTemplate?.label || "Community Showcase"}`}
                />

                <div className="form-text">
                  Optional. Live Example URLs use this title as their slug.
                </div>
              </div>
            </div>

            <div className="row g-4 mt-1">
              <div className="col-12 col-md-4">
                <label className="form-label" htmlFor="demo-owner-name">
                  Event Owner Name*
                </label>

                <input
                  id="demo-owner-name"
                  className="form-control"
                  type="text"
                  value={eventOwnerName}
                  readOnly
                  required
                />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label" htmlFor="demo-owner-email">
                  Event Owner Email*
                </label>

                <input
                  id="demo-owner-email"
                  className="form-control"
                  type="email"
                  value={eventOwnerEmail}
                  readOnly
                  required
                />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label" htmlFor="demo-owner-phone">
                  Event Owner Phone*
                </label>

                <input
                  id="demo-owner-phone"
                  className="form-control"
                  type="tel"
                  value={eventOwnerPhone}
                  readOnly
                  required
                />
              </div>
            </div>

            <div className="form-text mt-1">
              Customer demo owner details are fixed for this builder.
            </div>

            <div className="row g-4 mt-1">
              <div className="col-12 col-md-4">
                <label className="form-label" htmlFor="demo-start-date">
                  Start Date*
                </label>

                <input
                  id="demo-start-date"
                  className="form-control"
                  type="date"
                  value={eventStartDate}
                  max={
                    selectedIntent === "live_example"
                      ? getYesterdayInputValue()
                      : undefined
                  }
                  onChange={(event) => setEventStartDate(event.target.value)}
                  required
                />

                {selectedIntent === "live_example" && (
                  <div className="form-text">
                    Live Examples must use a date in the past.
                  </div>
                )}
              </div>

              <div className="col-12 col-md-8">
                <label className="form-label" htmlFor="demo-event-location">
                  Event Location*
                </label>

                <input
                  id="demo-event-location"
                  className="form-control"
                  type="text"
                  value={eventLocation}
                  onChange={(event) => setEventLocation(event.target.value)}
                  placeholder="Venue, city, or city/state"
                  required
                />
              </div>
            </div>

            <div className="row g-4 mt-1">
              <div className="col-12 col-md-6 col-lg-3">
                <label
                  className="form-label"
                  htmlFor="demo-industry"
                >
                  Event Type
                </label>

                <select
                  id="demo-industry"
                  className="form-select"
                  value={selectedIndustry}
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                >
                  {Object.values(DEMO_INDUSTRIES).map((industry) => (
                    <option
                      key={industry.key}
                      value={industry.key}
                      disabled={industry.enabled === false}
                    >
                      {industry.emoji} {industry.label}
                    </option>
                  ))}
                </select>

                <div className="form-text">
                  {industryTemplate?.description}
                  <br />
                  Entries: {industryTemplate?.entryLabel || "Entry"} · Categories:{" "}
                  {industryTemplate?.categoryLabel || "Category"} · Voters:{" "}
                  {industryTemplate?.voterLabel || "Voter"}
                </div>
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label
                  className="form-label"
                  htmlFor="demo-type"
                >
                  Demo Type
                </label>

                <select
                  id="demo-type"
                  className="form-select"
                  value={selectedIntent}
                  onChange={(e) => setSelectedIntent(e.target.value)}
                >
                  {Object.values(DEMO_INTENTS).map((intent) => (
                    <option key={intent.key} value={intent.key}>
                      {intent.emoji} {intent.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label
                  className="form-label"
                  htmlFor="demo-voting-engine"
                >
                  Voting Engine
                </label>

                <select
                  id="demo-voting-engine"
                  className="form-select"
                  value={selectedVotingType}
                  onChange={(e) => setSelectedVotingType(e.target.value)}
                  disabled={Boolean(industryTemplate?.scoring?.votingType)}
                >
                  {VOTING_TYPE_OPTIONS
                    .filter((type) => type.value !== "scorecard")
                    .map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                </select>
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label
                  className="form-label"
                  htmlFor="demo-size"
                >
                  Demo Size
                </label>

                <select
                  id="demo-size"
                  className="form-select"
                  value={selectedDemoSize}
                  onChange={(e) => setSelectedDemoSize(e.target.value)}
                  disabled={hasFixedExperienceSize}
                >
                  {Object.values(DEMO_SIZES).map((size) => (
                    <option key={size.key} value={size.key}>
                      {size.label} ({size.entryCount} entries)
                    </option>
                  ))}
                </select>
                {hasFixedExperienceSize && (
                  <div className="form-text">
                    This template has {industryTemplate.fixedEntryCount} fixed entries.
                  </div>
                )}
              </div>
            </div>

            <div className="d-flex flex-wrap justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-success"
                onClick={handleCreateDemoEvent}
                disabled={processing}
              >
                🧪 Create Demo Event
              </button>
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <button
              type="button"
              className="btn btn-link text-dark text-decoration-none p-0 w-100 d-flex justify-content-between align-items-center"
              aria-expanded={openConfigurationPanel === "powerups"}
              aria-controls="power-ups-panel"
              onClick={() => setOpenConfigurationPanel((current) =>
                current === "powerups" ? "" : "powerups"
              )}
            >
              <strong>Power Ups</strong>
              <span aria-hidden="true">
                {openConfigurationPanel === "powerups" ? "−" : "+"}
              </span>
            </button>
          </div>

          {openConfigurationPanel === "powerups" && (
            <div id="power-ups-panel" className="card-body">
              <p className="text-muted">
                Selected Power Ups are enabled and filled with industry-matched
                demo content, making each Experience ready for walkthroughs and
                customer presentations. Volunteers are included in the Core Package.
              </p>

              <div className="row g-3">
                {DEMO_ADDONS.map((addOn) => (
                  <div key={addOn.key} className="col-12 col-md-6 col-lg-4">
                    <div className={`border rounded p-3 h-100 ${addOn.disabled ? "bg-light" : ""}`}>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`addon-${addOn.key}`}
                          checked={selectedAddOns[addOn.key] === true}
                          disabled={addOn.disabled === true}
                          onChange={(event) =>
                            setSelectedAddOns((previous) => ({
                              ...previous,
                              [addOn.key]: event.target.checked,
                            }))
                          }
                        />

                        <label
                          className="form-check-label fw-semibold"
                          htmlFor={`addon-${addOn.key}`}
                        >
                          {addOn.label}
                        </label>
                      </div>

                      <div className="small text-muted mt-2">
                        {addOn.description}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <button
              type="button"
              className="btn btn-link text-dark text-decoration-none p-0 w-100 d-flex justify-content-between align-items-center"
              aria-expanded={openConfigurationPanel === "core"}
              aria-controls="core-package-panel"
              onClick={() => setOpenConfigurationPanel((current) =>
                current === "core" ? "" : "core"
              )}
            >
              <strong>Core Package Data</strong>
              <span aria-hidden="true">
                {openConfigurationPanel === "core" ? "−" : "+"}
              </span>
            </button>
          </div>

          {openConfigurationPanel === "core" && (
            <div id="core-package-panel" className="card-body">
              <p className="text-muted">
                The current demo helper creates the core event data needed to test
                LGV voting workflows. This checklist documents the intended Core Package
                coverage for the Demo Engine.
              </p>

              <div className="row g-2">
                {CORE_PACKAGE_ITEMS.map((item) => (
                  <div key={item} className="col-12 col-md-6 col-lg-4">
                    <div className="border rounded p-2 bg-light">
                      ✅ {item}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {demoReport && (
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white">
              <strong>✅ Demo Engine Report</strong>
            </div>

            <div className="card-body">
              <div className="row g-3 mb-4">
                <div className="col-12 col-md-3">
                  <div className="border rounded p-3 h-100 bg-light">
                    <div className="text-muted small">Event Type</div>
                    <div className="fw-bold">
                      {demoReport.config?.industryLabel || "Car Show"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-md-3">
                  <div className="border rounded p-3 h-100 bg-light">
                    <div className="text-muted small">Voting Engine</div>
                    <div className="fw-bold">
                      {demoReport.config?.votingEngine || "—"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-md-3">
                  <div className="border rounded p-3 h-100 bg-light">
                    <div className="text-muted small">Demo Size</div>
                    <div className="fw-bold">
                      {demoReport.config?.size || "small"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-md-3">
                  <div className="border rounded p-3 h-100 bg-light">
                    <div className="text-muted small">Package</div>
                    <div className="fw-bold">
                      {demoReport.config?.package || "core"}
                    </div>
                  </div>
                </div>
              </div>

              <h5 className="mb-3">Created</h5>

              <div className="row g-2 mb-4">
                {Object.entries(demoReport.created || {}).map(([key, value]) => (
                  <div key={key} className="col-12 col-md-6 col-lg-4">
                    <div className="border rounded p-2 bg-light">
                      {value ? "✅" : "⬜"} {key}
                      {typeof value === "number" && value > 0 ? ` (${value})` : ""}
                    </div>
                  </div>
                ))}
              </div>

              <h5 className="mb-3">PowerUps</h5>

              <div className="row g-2 mb-4">
                {Object.entries(demoReport.addOns || {}).map(([key, value]) => (
                  <div key={key} className="col-12 col-md-6 col-lg-4">
                    <div className="border rounded p-2 bg-light">
                      {renderReportValue(value)} {key}
                    </div>
                  </div>
                ))}
              </div>

                {demoReport.pipelineResults?.length > 0 && (
                <>
                    <h5 className="mb-3">Pipeline Results</h5>

                    <div className="row g-2 mb-4">
                    {demoReport.pipelineResults.map((stepResult) => (
                        <div
                        key={stepResult.step || stepResult.id || stepResult.label}
                        className="col-12 col-md-6 col-lg-4"
                        >
                        <div className="border rounded p-3 h-100 bg-light">
                            <div className="fw-semibold">
                            {stepResult.success ? "✅" : "❌"} {stepResult.label}
                            </div>

                            <div className="small text-muted mt-1">
                            {stepResult.message}
                            </div>

                            <div className="small mt-2">
                            Created: <strong>{renderReportValue(stepResult.created)}</strong>
                            </div>

                            <div className="small text-muted">
                            {stepResult.elapsedMs} ms
                            </div>
                        </div>
                        </div>
                    ))}
                    </div>
                </>
                )}

              <h5 className="mb-3">Links</h5>

              <div className="d-flex flex-wrap gap-2">
                {demoReport.links?.publicUrl && (
                  <Link
                    to={demoReport.links.publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-success"
                  >
                    Open Public Demo
                  </Link>
                )}

                {demoReport.links?.managerUrl && (
                  <Link
                    to={demoReport.links.managerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-secondary"
                  >
                    Open Manager Event
                  </Link>
                )}
              </div>

              {demoReport.messages?.length > 0 && (
                <div className="alert alert-info mt-4 mb-0">
                  {demoReport.messages.map((message, index) => (
                    <div key={`demo-message-${index}`}>
                      {message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="card shadow-sm">
          <div className="card-header bg-white d-flex justify-content-between align-items-center">
            <strong>Demo Sites</strong>
            <span className="badge bg-secondary">{demoSites.length}</span>
          </div>

          <div className="card-body p-0">
            {demoSitesLoading ? (
              <div className="p-4 text-muted">Loading demo sites...</div>
            ) : demoSites.length === 0 ? (
              <div className="p-4 text-muted">
                No demo sites have been created.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Title</th>
                      <th>Event Type</th>
                      <th>Demo Type</th>
                      <th>URL</th>
                      <th>Created</th>
                      <th>Expires</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {demoSites.map((site) => (
                      <tr key={site.id}>
                        <td className="fw-semibold">{site.name || "Untitled Demo"}</td>
                        <td>{site.demoIndustryLabel || site.demoIndustry || "—"}</td>
                        <td>
                          {DEMO_INTENTS[site.demoIntent]?.label ||
                            site.demoIntent ||
                            (site.slug?.startsWith("demo-")
                              ? "Legacy Demo"
                              : "—")}
                        </td>
                        <td>
                          {site.slug ? (
                            <Link
                              to={`/${site.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              /{site.slug}
                            </Link>
                          ) : "—"}
                        </td>
                        <td>
                          {formatTimestamp(
                            site.demoCreatedAt || site.createdAt
                          )}
                        </td>
                        <td>
                          {site.demoTemporary
                            ? formatTimestamp(site.demoExpiresAt, "Pending")
                            : "Does not expire"}
                        </td>
                        <td className="text-end">
                          <div className="btn-group btn-group-sm">
                            <Link
                              to={`/event/${site.slug || site.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-outline-secondary"
                            >
                              Edit
                            </Link>

                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={() => handleDeleteDemoSite(site)}
                            >
                              Delete Entire Event
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoBuilder;
